import { useEffect, useRef, useState, useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  type CalendarConnection,
  buildOAuthStartUrl,
  disconnectCalendar,
  fetchMyConnections,
} from '../api/crm'

type Provider = 'google' | 'microsoft'

const PROVIDER_LABELS: Record<Provider, string> = {
  google: 'Google Calendar',
  microsoft: 'Microsoft Outlook',
}

const providers: Provider[] = ['google', 'microsoft']

function statusLabel(c: CalendarConnection): string {
  if (c.status === 'active') {
    if (!c.last_synced_at) return 'Syncing your calendar…'
    return `Last synced ${new Date(c.last_synced_at).toLocaleString()}`
  }
  if (c.status === 'token_expired') return 'Token expired — reconnect to resume sync'
  return 'Disabled'
}

export default function ConnectedCalendars() {
  const [connections, setConnections] = useState<CalendarConnection[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [disconnecting, setDisconnecting] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)

  const [searchParams, setSearchParams] = useSearchParams()

  // ── Load connections ──────────────────────────────────────────────────────

  const loadConnections = useCallback(async (signal?: AbortSignal) => {
    try {
      const data = await fetchMyConnections(signal)
      setConnections(data)
      setError(null)
    } catch (err) {
      if ((err as Error).name !== 'AbortError') {
        setError('Failed to load connected calendars.')
      }
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    const controller = new AbortController()
    loadConnections(controller.signal)
    return () => controller.abort()
  }, [loadConnections])

  // ── Poll while any active connection is still on its initial sync ─────────

  const isSyncPending = connections.some((c) => c.status === 'active' && !c.last_synced_at)
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)

  useEffect(() => {
    if (!isSyncPending) {
      if (pollRef.current) clearInterval(pollRef.current)
      return
    }
    pollRef.current = setInterval(() => loadConnections(), 5000)
    return () => { if (pollRef.current) clearInterval(pollRef.current) }
  }, [isSyncPending, loadConnections])

  // ── Handle post-OAuth redirect ────────────────────────────────────────────

  useEffect(() => {
    const connected = searchParams.get('calendar_connected')
    const calError = searchParams.get('calendar_error')

    if (connected) {
      const label = PROVIDER_LABELS[connected as Provider] ?? connected
      setSuccessMessage(`${label} connected successfully.`)
      const next = new URLSearchParams(searchParams)
      next.delete('calendar_connected')
      setSearchParams(next, { replace: true })
      // Refresh so the new connection shows up immediately
      loadConnections()
    }

    if (calError) {
      setError('Calendar connection failed. Please try again.')
      const next = new URLSearchParams(searchParams)
      next.delete('calendar_error')
      setSearchParams(next, { replace: true })
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) // Only on mount — intentional. searchParams/setSearchParams in deps causes infinite loops.

  // ── Actions ───────────────────────────────────────────────────────────────

  function handleConnect(provider: Provider) {
    try {
      window.location.href = buildOAuthStartUrl(provider)
    } catch {
      setError('You must be logged in to connect a calendar.')
    }
  }

  async function handleDisconnect(connection: CalendarConnection) {
    setDisconnecting(connection.id)
    try {
      await disconnectCalendar(connection.id)
      setConnections((prev) => prev.filter((c) => c.id !== connection.id))
    } catch {
      setError('Failed to disconnect calendar. Please try again.')
    } finally {
      setDisconnecting(null)
    }
  }

  // ── Render ────────────────────────────────────────────────────────────────

  if (loading) return <p className="connected-calendars-loading">Loading calendars…</p>


  return (
    <div className="connected-calendars">
      <div className="card-header">
        <div>
          <p className="eyebrow">Integrations</p>
          <h2>Connected Calendars</h2>
        </div>
      </div>

      <div className="section-shell" style={{ marginTop: '8px' }}>
        {successMessage && (
          <p className="connected-calendars-success">{successMessage}</p>
        )}
        {error && (
          <p className="connected-calendars-error">{error}</p>
        )}

        <div className="connected-calendars-list">
          {providers.map((provider) => {
            const connection = connections.find((c) => c.provider === provider && c.status !== 'revoked')
            const isOn = !!connection
            return (
              <div key={provider} className="settings-toggle-row" style={{ marginBottom: '16px' }}>
                <div>
                  <div className="settings-toggle-label">{PROVIDER_LABELS[provider]}</div>
                  <div className="muted">
                    {connection ? (
                      <span
                        style={connection.status === 'active' && !connection.last_synced_at
                          ? { color: '#6366f1', animation: 'pulse 1.5s ease-in-out infinite' }
                          : undefined}
                      >
                        {statusLabel(connection)}
                      </span>
                    ) : (
                      `Connect your ${PROVIDER_LABELS[provider]} account.`
                    )}
                  </div>
                </div>
                <button
                  type="button"
                  className={`settings-switch ${isOn ? 'on' : 'off'}`}
                  onClick={() => {
                    if (connection) handleDisconnect(connection)
                    else handleConnect(provider)
                  }}
                  disabled={!!(connection && disconnecting === connection.id)}
                  aria-pressed={isOn}
                >
                  <span className="settings-switch-thumb" />
                  {!isOn ? <span className="settings-switch-status">Off</span> : null}
                </button>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}
